import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import {
  applyActionCode,
  confirmPasswordReset,
  verifyPasswordResetCode,
} from "firebase/auth";
import { auth } from "./firebase";

export default function VerifyEmail() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const mode = searchParams.get("mode");
  const oobCode = searchParams.get("oobCode");

  const [status, setStatus] = useState("loading");
  const [message, setMessage] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  useEffect(() => {
    const handleActionCode = async () => {
      if (!oobCode) {
        setStatus("error");
        setMessage("This link is missing its action code.");
        return;
      }

      try {
        if (mode === "resetPassword") {
          await verifyPasswordResetCode(auth, oobCode);
          setStatus("ready");
        } else {
          await applyActionCode(auth, oobCode);
          setStatus("success");
          setMessage("Your email has been verified successfully!");
        }
      } catch (error) {
        console.error(error);
        setStatus("error");
        if (error.code === "auth/expired-action-code") {
          setMessage("This link has expired. Request a new one.");
        } else if (error.code === "auth/invalid-action-code") {
          setMessage("This link is invalid or has already been used.");
        } else {
          setMessage("Something went wrong. Please try again.");
        }
      }
    };

    handleActionCode();
  }, [mode, oobCode]);

  const handlePasswordReset = async (event) => {
    event.preventDefault();

    if (password !== confirmPassword) {
      setMessage("The passwords do not match.");
      return;
    }

    setStatus("submitting");
    setMessage("");

    try {
      await confirmPasswordReset(auth, oobCode, password);
      setStatus("success");
      setMessage("Your password has been reset successfully.");
    } catch (error) {
      console.error(error);
      if (error.code === "auth/expired-action-code" || error.code === "auth/invalid-action-code") {
        setStatus("error");
        setMessage("This link is invalid or has expired. Request a new password-reset email.");
      } else {
        setStatus("ready");
        setMessage("Could not reset your password. Please try again.");
      }
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.card}>

        {/* Top accent bar */}
        <div style={styles.accentBar} />

        <div style={styles.body}>
          <div style={styles.iconWrap}>
            {(status === "loading" || status === "submitting") && <i className="fa-solid fa-spinner fa-spin" style={styles.icon} />}
            {status === "success" && <i className="fa-solid fa-circle-check" style={{ ...styles.icon, color: "#0A9396" }} />}
            {status === "error"   && <i className="fa-solid fa-circle-xmark" style={{ ...styles.icon, color: "#EF4444" }} />}
          </div>

          <h2 style={styles.title}>{mode === "resetPassword" ? "Reset your password" : "Email Verification"}</h2>

          {status === "loading" && (
            <p style={styles.text}>{mode === "resetPassword" ? "Checking your password-reset link…" : "Verifying your email address…"}</p>
          )}

          {status === "ready" && mode === "resetPassword" && (
            <form onSubmit={handlePasswordReset}>
              <label style={styles.label} htmlFor="new-password">New password</label>
              <input
                id="new-password"
                type="password"
                autoComplete="new-password"
                minLength={6}
                required
                value={password}
                onChange={event => setPassword(event.target.value)}
                style={styles.input}
              />
              <label style={styles.label} htmlFor="confirm-password">Confirm new password</label>
              <input
                id="confirm-password"
                type="password"
                autoComplete="new-password"
                minLength={6}
                required
                value={confirmPassword}
                onChange={event => setConfirmPassword(event.target.value)}
                style={styles.input}
              />
              {message && <p style={{ ...styles.text, color: "#EF4444" }}>{message}</p>}
              <button type="submit" style={styles.button}>Update password</button>
            </form>
          )}

          {status === "submitting" && (
            <p style={styles.text}>Updating your password…</p>
          )}

          {status === "success" && (
            <>
              <p style={{ ...styles.text, color: "#0A9396", fontWeight: "600" }}>
                {message}
              </p>
              <button
                style={styles.button}
                onClick={() => navigate("/")}
                onMouseOver={e => (e.currentTarget.style.background = "#087879")}
                onMouseOut={e => (e.currentTarget.style.background = "#0A9396")}
              >
                Continue to site
              </button>
            </>
          )}

          {status === "error" && (
            <p style={{ ...styles.text, color: "#EF4444", fontWeight: "600" }}>
              {message}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

const styles = {
  container: {
    minHeight: "100vh",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    background: "var(--off-white)",
    padding: "24px",
  },
  card: {
    background: "#fff",
    borderRadius: "18px",
    boxShadow: "0 8px 28px rgba(0,0,0,0.10)",
    width: "100%",
    maxWidth: "400px",
    overflow: "hidden",
    border: "1px solid var(--gray-200)",
  },
  accentBar: {
    height: "4px",
    background: "linear-gradient(90deg, #0A9396, #087879)",
  },
  body: {
    padding: "40px 36px",
    textAlign: "center",
  },
  iconWrap: {
    marginBottom: "16px",
  },
  icon: {
    fontSize: "40px",
    lineHeight: "1",
  },
  title: {
    fontSize: "22px",
    fontWeight: "800",
    color: "var(--gray-900)",
    margin: "0 0 14px",
    letterSpacing: "-0.02em",
  },
  text: {
    fontSize: "15px",
    color: "var(--gray-500)",
    lineHeight: "1.65",
    margin: "0 0 18px",
  },
  label: {
    display: "block",
    textAlign: "left",
    fontSize: "14px",
    fontWeight: "600",
    color: "var(--gray-700)",
    margin: "0 0 6px",
  },
  input: {
    display: "block",
    width: "100%",
    boxSizing: "border-box",
    padding: "12px",
    margin: "0 0 18px",
    border: "1px solid var(--gray-300)",
    borderRadius: "6px",
    font: "inherit",
  },
  button: {
    marginTop: "8px",
    width: "100%",
    padding: "13px",
    border: "none",
    background: "#0A9396",
    color: "#fff",
    borderRadius: "8px",
    fontSize: "15px",
    fontWeight: "700",
    fontFamily: "inherit",
    cursor: "pointer",
    transition: "background 0.24s ease",
    boxShadow: "0 2px 10px rgba(10,147,150,0.38)",
  },
};
